import { basename } from "../utils/Url";
import { parseJSON } from "../utils/date";
import Cookies from "js-cookie";
import moment from "moment";
import { ApiList, ApiObject } from "../utils/Api";
import {
  defaultApiBuilder,
  defaultApiWithCustomUrlBuilder,
} from "../utils/axios/apiBuilder";
import { getOrderQueryDirection } from "../utils/ordering";

export const ManagerChangeStatus = {
  ACCEPTED: "accepted",
  PENDING: "pending",
};

const apiEmployeeWithCustomUrl = defaultApiWithCustomUrlBuilder(parseJSON);
const apiEmployee = defaultApiBuilder(parseJSON);
const apiEmployeeList = (paginated) =>
  defaultApiBuilder((response) => ApiList(response, null, paginated));
const apiEmployeeObject = defaultApiBuilder(ApiObject);

export const AxiosEmployeeAPI = {
  createCompleteEmployee: async ({
    first_name,
    last_name,
    username,
    email,
    employee_code,
    entrance_type,
    start_date,
    gender,
    cpf,
    birth_date,
    company_referal,
    work_type,
    work_city,
    area,
    position,
    manager,
    corporation,
    branch_corporation,
    registration_code,
    contract_type,
    systems_permissions = [],
    phone_number,
    preferred_channel,
  }) => {
    const response = await apiEmployee.post(`/employees/create_complete/`, {
      first_name,
      last_name,
      username,
      email,
      employee_code,
      entrance_type,
      start_date,
      area,
      position,
      manager,
      systems_permissions,
      gender,
      cpf,
      birth_date,
      company_referal,
      work_type,
      work_city,
      contract_type,
      corporation,
      branch_corporation,
      registration_code,
      phone_number,
      preferred_channel,
    });
    return response.data;
  },
  edit: async ({ employeeId, data }) => {
    const response = await apiEmployee.patch(
      `/employees/${employeeId}/edit/`,
      data,
    );
    return response.data;
  },
  getContractTypes: async ({ signal } = {}) => {
    const response = await apiEmployeeList(true).get(`/contract_types/`, {
      params: { page_size: 1000 },
      signal,
    });
    return response.data;
  },
  getAllAccessible: async (
    { active = true, search = "", page = 1, page_size = 20 },
    { signal },
  ) => {
    const params = {
      expand: ["_extra", "name", "uuid", "user"].join(","),
      active,
      page,
      page_size,
    };
    if (search) params.search = search;

    const response = await apiEmployeeList(true).get(`/employees/`, {
      params,
      signal,
    });
    return response.data;
  },
  getById: async ({ employee_id, expand }, { signal }) => {
    const params = {
      expand,
    };
    const response = await apiEmployeeObject.get(`/employees/${employee_id}/`, {
      params,
      signal,
    });
    return { ...response.data, id: employee_id };
  },
  getByUUID: async ({ employee_uuid, expand }, { signal }) => {
    const params = {
      expand,
    };
    const response = await apiEmployeeObject.get(
      `/employees/${employee_uuid}/`,
      { params, signal },
    );
    return { ...response.data };
  },
  getCurrent: async ({ signal }) => {
    const params = {
      expand: ["_extra", "name"].join(","),
    };
    const response = await apiEmployeeObject.get(
      `/employees/current_employee/`,
      { params, signal },
    );
    return response.data;
  },
  getAuthorizedEmployees: async ({ signal }) => {
    const params = {
      expand: ["_extra", "name"].join(","),
    };
    const response = await apiEmployeeList(false).get(`/authorizations/`, {
      params,
      signal,
    });
    return response.data;
  },
  changeActiveEmployee: async ([{ employee_code }], _, { signal }) => {
    const response = await apiEmployee.post(
      `/authorizations/change_active_employee/`,
      {
        employee_code: employee_code,
      },
      { signal },
    );
    return response.data;
  },
  getTerminationTypes: async ({ signal }) => {
    const response = await apiEmployee.get(`/employees/termination_types/`, {
      signal,
    });
    return response.data;
  },
  terminateEmployee: async (
    { employee_code, end_date, type, reason },
    { signal },
  ) => {
    const response = await apiEmployee.post(
      `/employees/${employee_code}/deactivate/`,
      {
        end_date: end_date,
        termination_type: type,
        termination_reason: reason,
      },
      { signal },
    );
    return response.data;
  },
};

export const AxiosEmployeeManagerAPI = {
  getTeam: async ({ employee_id }, { signal }) => {
    const params = {
      expand: ["_extra", "name"].join(","),
    };
    const response = await apiEmployeeList(false).get(
      `/employees/${employee_id}/direct_team/`,
      { params, signal },
    );
    return response.data;
  },
  getManagers: async (
    { employee_id, ordering = null, page = 1 },
    { signal },
  ) => {
    const params = {
      expand: ["manager.name", "employee.name"].join(","),
      employee: employee_id,
      page,
    };
    if (ordering) params.ordering = getOrderQueryDirection(ordering);

    const response = await apiEmployee.get(`/employee_managers/`, {
      params,
      signal,
    });
    return response.data;
  },
  changeManager: async (
    [{ employee, new_manager, change_date, review_access }],
    { signal },
  ) => {
    const response = await apiEmployeeWithCustomUrl.post(
      employee._actions.change_manager,
      {
        manager: new_manager,
        start_date: moment(change_date).format("YYYY-MM-DD"),
        review_access: review_access,
      },
      { signal },
    );
    return response.data;
  },
};

export const AxiosEmployeeRecordApi = {
  getForEmployee: async (
    { employee_id, ordering = null, expand = [], page = 1 },
    { signal },
  ) => {
    const params = {
      employee: employee_id,
      page,
      expand: expand.join(","),
    };
    if (ordering) params.ordering = getOrderQueryDirection(ordering);

    const response = await apiEmployee.get(`/employee_records/`, {
      params,
      signal,
    });
    return response.data;
  },
};

export class AuthIntegrationApi {
  static async authRequest(path, method, params = {}, data = {}) {
    const response = await apiEmployee.post("/integration_auth/auth_request/", {
      path,
      method,
      params,
      data,
    });
    return response.data;
  }

  static async getSystems() {
    const response = await AuthIntegrationApi.authRequest(
      "mindsight_sync_systems/",
      "GET",
      { expand: "system_info" },
    );
    return response;
  }

  static async getPermissions(user_uuid) {
    const response = await AuthIntegrationApi.authRequest(
      `user_system_access/${user_uuid}/`,
      "GET",
    );
    return response["systems"];
  }

  static async editPermissions(data) {
    const response = await AuthIntegrationApi.authRequest(
      `user_system_access/update_or_create/`,
      "POST",
      {},
      data,
    );
    return response;
  }
}

export const ImportEmployeeAPI = {
  create_import: async (data) => {
    const response = await apiEmployee.post(`/upload_create_employee/`, data);
    return response.data;
  },
  edit_import: async (data) => {
    const response = await apiEmployee.post(`/upload_edit_employee/`, data);
    return response.data;
  },
};
